import React, { useEffect, useState } from "react";
import { api, friendlyError } from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { Pill } from "../lib/ui";
import { Plus, X, Package } from "lucide-react";
import { toast } from "sonner";

export default function Prodotti() {
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", internal_code: "", client_name: "", dosage_form: "", strength: "",
    markets: [], application_type: "", manufacturing_process: "", established_conditions: false, pacmp: false });

  const load = () => Promise.all([api.get("/products"), api.get("/clients")]).then(([p, c]) => { setProducts(p.data); setClients(c.data); });
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { await api.post("/products", { ...form, markets: form.markets }); toast.success("Prodotto creato"); setShow(false); load(); }
    catch (e) { toast.error(friendlyError(e)); }
  };

  return (
    <div className="ri-fade-in">
      <PageHeader overline="Product Regulatory Profile" title="Prodotti"
        subtitle="Profili prodotto con Established Conditions, PACMP e mercati registrati."
        actions={<button onClick={() => setShow(true)} data-testid="product-add-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-700 text-white text-sm hover:bg-blue-800"><Plus size={15} /> Nuovo prodotto</button>} />

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {products.map((p) => (
          <div key={p.id} data-testid={`product-card-${p.id}`} className="rounded-xl bg-white border border-slate-200 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <Package size={18} className="text-blue-600" />
              {p.is_demo && <span className="text-[0.6rem] font-mono px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">DEMO</span>}
            </div>
            <div className="font-head font-semibold mt-2">{p.name}</div>
            <div className="text-xs text-slate-400 font-mono">{p.internal_code}</div>
            <div className="text-sm text-slate-500 mt-1">{p.client_name}</div>
            <dl className="text-xs text-slate-600 mt-3 space-y-1">
              <div className="flex justify-between"><dt className="text-slate-400">Forma</dt><dd>{p.dosage_form}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Dosaggio</dt><dd>{p.strength}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Application</dt><dd>{p.application_type}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-400">Processo</dt><dd className="text-right">{p.manufacturing_process}</dd></div>
            </dl>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(p.markets || []).map((m) => <Pill key={m} color="#166534" bg="#DCFCE7">{m}</Pill>)}
            </div>
            <div className="flex gap-2 mt-2">
              {p.established_conditions && <Pill color="#9A3412" bg="#FFEDD5">Established Conditions</Pill>}
              {p.pacmp && <Pill color="#1E40AF" bg="#DBEAFE">PACMP</Pill>}
            </div>
          </div>
        ))}
      </div>

      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.6)" }}>
          <div className="bg-white rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head font-semibold text-lg">Nuovo prodotto</h3>
              <button onClick={() => setShow(false)}><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <input placeholder="Nome prodotto" data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Codice interno" value={form.internal_code} onChange={(e) => setForm({ ...form, internal_code: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                <select value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white">
                  <option value="">Cliente...</option>
                  {clients.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                <input placeholder="Dosage form" value={form.dosage_form} onChange={(e) => setForm({ ...form, dosage_form: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                <input placeholder="Strength" value={form.strength} onChange={(e) => setForm({ ...form, strength: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                <input placeholder="Application (NDA/BLA/MAA)" value={form.application_type} onChange={(e) => setForm({ ...form, application_type: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
                <input placeholder="Processo produttivo" value={form.manufacturing_process} onChange={(e) => setForm({ ...form, manufacturing_process: e.target.value })} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              </div>
              <input placeholder="Mercati (separati da virgola, es: eu,usa)" onChange={(e) => setForm({ ...form, markets: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.established_conditions} onChange={(e) => setForm({ ...form, established_conditions: e.target.checked })} /> Established Conditions</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.pacmp} onChange={(e) => setForm({ ...form, pacmp: e.target.checked })} /> PACMP</label>
              </div>
              <button onClick={create} data-testid="product-save-btn" className="w-full py-2.5 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800">Salva prodotto</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
